import React, { useState } from "react";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import HPButton from "@components/common/Button";
import HPInput from "@components/common/Input";
import type {
  VisualizationBase,
  VisualizationBaseCondition,
} from "@models/VisualizationBase";
import BaseConditionBuilder from "./BaseConditionBuilder";
import BaseConditionSummaryCard from "./BaseConditionSummaryCard";
import { ConditionAndDivider } from "./ConditionAndDivider";

interface CustomBaseContainerProps {
  datasetId: string | null;
  isDisabled?: boolean;
  base: VisualizationBase;
  onChange: (base: VisualizationBase) => void;
}

function createBlankCondition(): VisualizationBaseCondition {
  return {
    id: crypto.randomUUID(),
    questionId: "",
    questionType: "single",
    variableIds: [],
    valueIds: [],
  };
}

// The widget-scoped custom base editor: Base Name + AND-joined condition cards +
// inline builder. Emits the whole updated base via onChange.
const CustomBaseContainer: React.FC<CustomBaseContainerProps> = ({
  datasetId,
  isDisabled,
  base,
  onChange,
}) => {
  const [editingConditionId, setEditingConditionId] = useState<string | null>(null);
  const [draftCondition, setDraftCondition] = useState<VisualizationBaseCondition | null>(null);

  const isBuilderOpen = draftCondition !== null;

  const resetBuilder = () => {
    setEditingConditionId(null);
    setDraftCondition(null);
  };

  const openAddBuilder = () => {
    setEditingConditionId(null);
    setDraftCondition(createBlankCondition());
  };

  const openEditBuilder = (condition: VisualizationBaseCondition) => {
    setEditingConditionId(condition.id);
    setDraftCondition({ ...condition });
  };

  const handleSaveDraft = () => {
    if (!draftCondition) return;
    const isEditing = editingConditionId !== null;
    const conditions = isEditing
      ? base.conditions.map((c) => (c.id === draftCondition.id ? draftCondition : c))
      : [...base.conditions, draftCondition];
    onChange({ ...base, conditions });
    resetBuilder();
  };

  const handleDeleteCondition = (id: string) => {
    onChange({ ...base, conditions: base.conditions.filter((c) => c.id !== id) });
    if (editingConditionId === id) resetBuilder();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        mt: 2,
        p: 2,
        border: "1px solid #eee",
        borderRadius: 1,
      }}
    >
      <HPInput
        label="Base Name*"
        value={base.name}
        onChange={(e) => onChange({ ...base, name: e.target.value })}
        placeholder="Enter a name for this base"
        disabled={isDisabled}
      />

      {base.conditions.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {base.conditions.map((condition, index) => (
            <React.Fragment key={condition.id}>
              {index > 0 && <ConditionAndDivider />}
              <BaseConditionSummaryCard
                datasetId={datasetId as string}
                condition={condition}
                index={index}
                isDimmed={isBuilderOpen && editingConditionId !== condition.id}
                isHighlighted={editingConditionId === condition.id}
                disabled={isDisabled}
                onEdit={() => openEditBuilder(condition)}
                onDelete={() => handleDeleteCondition(condition.id)}
              />
            </React.Fragment>
          ))}
        </Box>
      )}

      {isBuilderOpen && draftCondition ? (
        <>
          {base.conditions.length > 0 && editingConditionId === null && <ConditionAndDivider />}
          <BaseConditionBuilder
            datasetId={datasetId as string}
            condition={draftCondition}
            excludedQuestionIds={base.conditions
              .filter((c) => c.id !== draftCondition.id)
              .map((c) => c.questionId)
              .filter(Boolean)}
            isDisabled={isDisabled}
            isEditing={editingConditionId !== null}
            onChange={setDraftCondition}
            onCancel={resetBuilder}
            onSave={handleSaveDraft}
          />
        </>
      ) : (
        <HPButton
          ariaLabel="Add base condition"
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openAddBuilder}
          disabled={isDisabled || !datasetId}
        >
          Add Condition
        </HPButton>
      )}
    </Box>
  );
};

export default CustomBaseContainer;
